import { differenceInSeconds, parseISO } from "date-fns";
import { UserAccount } from "src/types/User";
import isOrphan from "./isOrphan";

/**
 * Compatibility grace window for legacy signup records that predate the
 * explicit organizationAssignmentStatus field. New signups remain pending
 * until the server writes matched/no_match, regardless of elapsed time.
 */
export const ASSIGNMENT_GRACE_SECONDS = 5;

/**
 * True when the user is an orphan AND the user was created less than
 * ASSIGNMENT_GRACE_SECONDS ago. Caller uses this to render a loading
 * skeleton instead of bouncing to the waitlist while the trigger may
 * still be in flight.
 *
 * `now` is injectable for test determinism — defaults to `new Date()`.
 * Returns false when `createdAt` is missing (treat as past grace — real
 * orphan, not a brand-new signup).
 */
export default function isPendingAssignment(
  account: UserAccount,
  now: Date = new Date()
): boolean {
  if (!isOrphan(account)) return false;
  if (account.organizationAssignmentStatus === "pending") return true;
  if (account.organizationAssignmentStatus !== undefined) return false;
  if (!account.createdAt) return false;
  const elapsed = differenceInSeconds(now, parseISO(account.createdAt));
  return elapsed < ASSIGNMENT_GRACE_SECONDS;
}

/**
 * Milliseconds remaining in the assignment grace window for an orphan
 * user. Returns 0 when the user is already assigned, has no createdAt,
 * or the window has already elapsed.
 *
 * Callers use this to schedule a re-render at the moment the grace
 * window expires. The orphan branch of the auto-assign trigger writes
 * nothing to the user doc (only a log), so onSnapshot never fires for
 * a true orphan. Without a wall-clock timer the gate never transitions
 * from `pending` to `waitlist`.
 */
export function getRemainingGraceMs(
  account: UserAccount,
  now: Date = new Date()
): number {
  if (!isOrphan(account)) return 0;
  // Explicit states are snapshot-driven. Pending waits for the server write;
  // terminal states need no timer. Only legacy records use the grace timer.
  if (account.organizationAssignmentStatus !== undefined) return 0;
  if (!account.createdAt) return 0;
  const elapsedSeconds = differenceInSeconds(now, parseISO(account.createdAt));
  return Math.max(0, (ASSIGNMENT_GRACE_SECONDS - elapsedSeconds) * 1000);
}
